import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { Redirect, RouteComponentProps, Switch } from 'react-router';
import { withRouter, Route } from 'react-router-dom';
import { ForgotPasswordPage } from '../Pages/AuthPages/ForgotPasswordPage';
import { ForgotPasswordPageFacebook } from '../Pages/AuthPages/ForgotPasswordPageFacebook';
import { LoginPage } from '../Pages/AuthPages/LoginPage';
import { PasswordResetPage } from '../Pages/AuthPages/PasswordResetPage';
import { PrivacyPolicyPage } from '../Pages/AuthPages/PrivacyPolicyPage';
import { SignupPage } from '../Pages/AuthPages/SignupPage';
import { AddFamilyMemberPage } from '../Pages/FamiliesPage/AddFamilyMemberPage';
import { AddFamilyPage } from '../Pages/FamiliesPage/AddFamilyPage';
import { FamiliesPage } from '../Pages/FamiliesPage/FamiliesPage';
import { FamilyDetailPage } from '../Pages/FamiliesPage/FamilyDetailPage';
import { FamilyMemberPage } from '../Pages/FamiliesPage/FamilyMemberPage';
import { FamilySettingsPage } from '../Pages/FamiliesPage/FamilySettingsPage';
import { FreeForLifePromotion } from '../Pages/FreeForLife';
import { NotificationsPage } from '../Pages/NotificationsPage/NotificationsPage';
import { OnboardingAddFamily } from '../Pages/OnboardingPages/OnboardingAddFamily';
import { OnboardingAddPhotos } from '../Pages/OnboardingPages/OnboardingAddPhotos';
import { OnboardingCreateStory } from '../Pages/OnboardingPages/OnboardingCreateStory';
import { OnboardingFinished } from '../Pages/OnboardingPages/OnboardingFinished';
import { OnboardingIntro } from '../Pages/OnboardingPages/OnboardingIntro';
import { OnboardingShareStory } from '../Pages/OnboardingPages/OnboardingShareStory';
import { OnboardingWelcome } from '../Pages/OnboardingPages/OnboardingWelcome';
import { PhotoViewer } from '../Pages/PhotoViewer/PhotoViewer';
import { AddPhotosPage } from '../Pages/PhotosPage/AddPhotosPage';
import { PhotoDetail } from '../Pages/PhotosPage/PhotoDetail';
import { PhotosPage } from '../Pages/PhotosPage/PhotosPage';
import { ProfileSettings } from '../Pages/ProfileSettings/ProfileSettings';
import { AddPhotosToStory } from '../Pages/StoriesPage/AddPhotosToStory';
import { AddStoryPage } from '../Pages/StoriesPage/AddStoryPage';
import { ShareStoryModal } from '../Pages/StoriesPage/ShareStoryModal';
import { StoriesPage } from '../Pages/StoriesPage/StoriesPage';
import { StoryDetailPage } from '../Pages/StoriesPage/StoryDetailPage';
import { StorySettings } from '../Pages/StoriesPage/StorySettings';
import { TosPage } from '../Pages/TosPage';
import { ApplicationStore } from '../_stores/ApplicationStore';
import '../styles/App.css';
import * as styles from '../styles/styleObjects';
import { logAmplitudeEvent } from '../util/AmpltudeWrapper';
import { PrivateRoute } from './PrivateRoute';

interface IProps {
    store: ApplicationStore;
}

interface IState {
    currentRoute: string;
}

export class RouteFormats {
    public static shareStory(storyId: number): string {
        return `/share_story/${storyId}`;
    }
    public static storySettings(storyId: number): string {
        return `/storysettings/${storyId}`;
    }
}

export enum Routes {
    EMPTY = '/',
    LOGIN = '/login',
    SIGNUP = '/signup',
    RESET = '/reset',
    FORGOT = '/forgot',
    FORGOT_FACEBOOK = '/facebook-reset',
    FAMILIES = '/families',
    PHOTOS = '/photos',
    COLLECTIONS = '/collections',
    COLLECTION_DETAIL = '/collection/:id',
    ARTIFACT_DETAIL = '/artifact/:id',
    ARTIFACT = '/artifact',
    ALBUMS = '/albums',
    ADD_STORY = '/addstory',
    ADD_FAMILY = '/addfamily',
    FAMILY_DETAIL = '/familydetail/:id',
    FAMILY_MEMBER = '/familydetail/:id/familymember/:memberID',
    FAMILY_SETTINGS = '/familysettings/:id',
    ADD_FAMILY_MEMBER = '/addfamilymember/:id',
    ONBOARDING_INTRO = '/onboarding_intro',
    ONBOARDING_HOW_IT_WORKS = '/onboarding_howitworks',
    ONBOARDING_SETUP_PROFILE = '/onboarding_setupprofile',
    ONBOARDING_CREATE_STORY = '/onboarding_createstory',
    ONBOARDING_SHARE_STORY = '/onboarding_sharestory',
    ONBOARDING_CREATE_FAMILY = '/onboarding_createfamily',
    ONBOARDING_ADD_PHOTOS = '/onboarding_addphotos',
    ONBOARDING_FINISHED = '/onboarding_finished',
    PHOTO_DETAIL = '/photodetail/:id',
    PROFILE_SETTINGS = '/profilesettings',
    STORY_DETAIL = '/storydetail/:id',
    PHOTO_VIEWER = '/viewstory/:id/:photoID?',
    ADD_PHOTOS = '/addphotos',
    NOTIFICATIONS = '/notifications',
    ADD_PHOTOS_TO_STORY = '/addphotostostory/:id',
    SHARE_STORY = '/share_story/:id',
    VIEW_TOS = '/termsofservice',
    VIEW_PRIVACY_POLICY = '/privacypolicy',
    ONBOARDING_WELCOME = '/onboarding_welcome',
    FREE_FOR_LIFE_PROMOTION = '/freeforlife',
}

@inject('store')
@observer
class RouteControllerClass extends React.Component<IProps & RouteComponentProps<any>, IState> {
    public constructor(props: IProps & RouteComponentProps<any>) {
        super(props);

        console.assert(this.props.location !== undefined || this.props.location !== null, 'Location was undefined @ constructor of RouteController');
        this.state = {
            currentRoute: this.props.location!.pathname,
        };
    }

    private mainLoopId = setInterval(() => {
        const store = this.props.store.authorizationStore;
        //refreshes auth token if user has website open so we don't force log them out until they hit the button or close the site;
        store.refreshAuthToken();
        // TODO: LOGOUT
        // TODO: Test if this runs when tabbed out
    }, 60 * 60 * 1000);

    public componentDidMount(): void {
        logAmplitudeEvent('APP LOAD');
    }

    public componentWillUnmount(): void {
        clearInterval(this.mainLoopId);
    }

    public render() {
        if (this.props.store.authorizationStore.authIsResolved === false) {
            return null;
        }
        const isAuthenticated = this.props.store.authorizationStore.isAuthenticated;

        return (
            <div style={styles.fullWidthWhite}>
                <Route exact={true} path="/" component={LoginPage} />
                <Route exact={true} path={Routes.SIGNUP} component={SignupPage} />
                <Route exact={true} path={Routes.LOGIN} component={LoginPage} />
                <Route exact={true} path={Routes.FORGOT} component={ForgotPasswordPage} />
                <Route exact={true} path={Routes.FORGOT_FACEBOOK} component={ForgotPasswordPageFacebook} />
                <Route exact={true} path={Routes.RESET} component={PasswordResetPage} />
                <Route exact={true} path={Routes.VIEW_TOS} component={TosPage} />
                <Route exact={true} path={Routes.VIEW_PRIVACY_POLICY} component={PrivacyPolicyPage} />
                <Route exact={true} path={Routes.FREE_FOR_LIFE_PROMOTION} component={FreeForLifePromotion} />
                <PrivateRoute exact={true} to={Routes.ALBUMS} redirectTo={Routes.LOGIN} isAuthenticated={isAuthenticated} Component={StoriesPage} />
                <PrivateRoute exact={true} to={Routes.FAMILIES} redirectTo={Routes.LOGIN} isAuthenticated={isAuthenticated} Component={FamiliesPage} />
                <PrivateRoute exact={true} to={Routes.PHOTOS} redirectTo={Routes.LOGIN} isAuthenticated={isAuthenticated} Component={PhotosPage} />
                <PrivateRoute exact={true} to={Routes.ADD_STORY} redirectTo={Routes.LOGIN} isAuthenticated={isAuthenticated} Component={AddStoryPage} />
                <PrivateRoute exact={true} to={Routes.PHOTO_VIEWER} redirectTo={Routes.LOGIN} isAuthenticated={isAuthenticated} Component={PhotoViewer} />
                <PrivateRoute exact={true} to={Routes.ADD_PHOTOS} redirectTo={Routes.LOGIN} isAuthenticated={isAuthenticated} Component={AddPhotosPage} />
                <PrivateRoute
                    exact={true}
                    to={Routes.NOTIFICATIONS}
                    redirectTo={Routes.LOGIN}
                    isAuthenticated={isAuthenticated}
                    Component={NotificationsPage}
                />
                <Route exact={true} path={Routes.ONBOARDING_INTRO} component={OnboardingIntro} />
                <Route exact={true} path={Routes.ONBOARDING_CREATE_STORY} component={OnboardingCreateStory} />
                <Route exact={true} path={Routes.ONBOARDING_ADD_PHOTOS} component={OnboardingAddPhotos} />
                <Route exact={true} path={Routes.ONBOARDING_SHARE_STORY} component={OnboardingShareStory} />
                <Route exact={true} path={Routes.ONBOARDING_CREATE_FAMILY} component={OnboardingAddFamily} />
                <Route exact={true} path={Routes.ONBOARDING_FINISHED} component={OnboardingFinished} />
                <Route exact={true} path={Routes.ONBOARDING_WELCOME} component={OnboardingWelcome} />
                <PrivateRoute exact={true} to={Routes.PHOTO_DETAIL} redirectTo={Routes.LOGIN} isAuthenticated={isAuthenticated} Component={PhotoDetail} />
                <PrivateRoute exact={true} to={Routes.ADD_FAMILY} redirectTo={Routes.LOGIN} isAuthenticated={isAuthenticated} Component={AddFamilyPage} />
                <PrivateRoute exact={true} to={Routes.FAMILY_DETAIL} redirectTo={Routes.LOGIN} isAuthenticated={isAuthenticated} Component={FamilyDetailPage} />
                <PrivateRoute exact={true} to={Routes.FAMILY_MEMBER} redirectTo={Routes.LOGIN} isAuthenticated={isAuthenticated} Component={FamilyMemberPage} />
                <PrivateRoute
                    exact={true}
                    to={Routes.FAMILY_SETTINGS}
                    redirectTo={Routes.LOGIN}
                    isAuthenticated={isAuthenticated}
                    Component={FamilySettingsPage}
                />
                <PrivateRoute
                    exact={true}
                    to={Routes.ADD_FAMILY_MEMBER}
                    redirectTo={Routes.LOGIN}
                    isAuthenticated={isAuthenticated}
                    Component={AddFamilyMemberPage}
                />
                <PrivateRoute
                    exact={true}
                    to={Routes.PROFILE_SETTINGS}
                    redirectTo={Routes.LOGIN}
                    isAuthenticated={isAuthenticated}
                    Component={ProfileSettings}
                />
                <PrivateRoute exact={true} to={Routes.STORY_DETAIL} redirectTo={Routes.LOGIN} isAuthenticated={isAuthenticated} Component={StoryDetailPage} />
                <PrivateRoute
                    exact={true}
                    to={Routes.ADD_PHOTOS_TO_STORY}
                    redirectTo={Routes.LOGIN}
                    isAuthenticated={isAuthenticated}
                    Component={AddPhotosToStory}
                />
                <PrivateRoute exact={true} to={RouteFormats.storySettings(':id' as unknown as number)} redirectTo={Routes.LOGIN} isAuthenticated={isAuthenticated} Component={StorySettings} />
                <PrivateRoute exact={true} to={RouteFormats.shareStory(':storyId' as unknown as number)} redirectTo={Routes.LOGIN} isAuthenticated={isAuthenticated} Component={ShareStoryModal} />
                <Switch>
                    <Redirect from="/stories" to={Routes.ALBUMS} />
                </Switch>
            </div>
        );
    }
}

export const RouteController = withRouter<IProps & RouteComponentProps<any>, typeof RouteControllerClass>(RouteControllerClass);



// WEBPACK FOOTER //
// ./src/_controllers/RouteController.tsx